import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bottles, Catalog, type Bottle, type BottleCreate } from '@/lib/api';
import { Button, Drawer, Input, Icon, Skeleton, Textarea } from '@/design-system';
import { useDebounce } from '@/lib/hooks';
import { useToasts } from '@/lib/store';
import { formatPriceNok } from '@/lib/format';
import { BottleImage } from '@/components/BottleCard';
import { DuplicateBottleModal, findDuplicate, type DuplicateMatch } from '@/components/DuplicateBottleModal';
import { LocationPicker } from '@/components/LocationPicker';
import { UpgradeInlineNotice, isEntitlementError } from '@/components/UpgradeGate';

interface AddBottleDrawerProps {
  open: boolean;
  onClose: () => void;
  /** Pre-fill if user came from the scanner */
  prefill?: Partial<BottleCreate>;
  /** When set, the drawer becomes an edit drawer for this bottle. */
  existing?: Bottle;
  onAdded?: (bottleId: string) => void;
  onUpdated?: (bottleId: string) => void;
}

function DrinkWindowRange({
  from,
  to,
  onChange,
}: {
  from?: number;
  to?: number;
  onChange: (from: number | undefined, to: number | undefined) => void;
}) {
  const currentYear = new Date().getFullYear();
  const min = currentYear - 10;
  const max = currentYear + 30;
  const fromVal = from ?? currentYear;
  const toVal = to ?? currentYear + 5;

  const setFrom = (v: number) => onChange(v, Math.max(v, toVal));
  const setTo = (v: number) => onChange(Math.min(fromVal, v), v);
  const reset = () => onChange(undefined, undefined);

  const isSet = from != null || to != null;
  const span = isSet ? toVal - fromVal : 0;

  return (
    <div className="grid gap-2 rounded-md border border-line bg-ink-soft p-3">
      <div className="flex items-baseline justify-between">
        <span className="text-xs uppercase tracking-widest text-mist">Drikkevindu</span>
        <span className="font-serif text-sm text-parchment">
          {isSet ? `${fromVal} – ${toVal}` : 'Ikke angitt'}
          {isSet && span > 0 && (
            <span className="ml-2 text-[10px] uppercase tracking-widest text-mist">
              ({span} år)
            </span>
          )}
        </span>
      </div>
      <div className="grid gap-1">
        <label className="text-[10px] uppercase tracking-widest text-mist">Fra {fromVal}</label>
        <input
          type="range"
          min={min}
          max={max}
          step={1}
          value={fromVal}
          onChange={(e) => setFrom(Number(e.target.value))}
          aria-label="Drikkes fra år"
          className="w-full accent-[color:var(--ui-color-candle)]"
        />
      </div>
      <div className="grid gap-1">
        <label className="text-[10px] uppercase tracking-widest text-mist">Til {toVal}</label>
        <input
          type="range"
          min={min}
          max={max}
          step={1}
          value={toVal}
          onChange={(e) => setTo(Number(e.target.value))}
          aria-label="Drikkes til år"
          className="w-full accent-[color:var(--ui-color-candle)]"
        />
      </div>
      {isSet && (
        <button
          type="button"
          onClick={reset}
          className="justify-self-end text-[10px] uppercase tracking-widest text-mist hover:text-candle"
        >
          Nullstill
        </button>
      )}
    </div>
  );
}

export function AddBottleDrawer({ open, onClose, prefill, existing, onAdded, onUpdated }: AddBottleDrawerProps) {
  const isEdit = Boolean(existing);
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { push } = useToasts();
  const [search, setSearch] = useState('');
  const debounced = useDebounce(search, 300);
  const [draft, setDraft] = useState<BottleCreate>({});
  const [duplicateMatch, setDuplicateMatch] = useState<DuplicateMatch | null>(null);

  useEffect(() => {
    if (!open) return;
    if (existing) {
      setDraft({
        name: existing.name,
        producer: existing.producer,
        vintage: existing.vintage,
        country: existing.country,
        region: existing.region,
        grapeVarieties: existing.grapeVarieties,
        qty: existing.qty,
        status: existing.status,
        rackId: existing.rackId,
        slot: existing.slot,
        locationId: existing.locationId,
        barcode: existing.barcode,
        photoUrl: existing.photoUrl,
        notes: existing.notes,
        drinkFromYear: existing.drinkFromYear,
        drinkToYear: existing.drinkToYear,
        tags: existing.tags,
        catalogId: existing.catalogId,
        productId: existing.productId,
      });
    } else if (prefill) {
      setDraft(prefill);
    }
  }, [open, prefill, existing]);

  const catalogQ = useQuery({
    queryKey: ['add-bottle.catalog.search', debounced],
    queryFn: () => Catalog.search(debounced, 8),
    enabled: open && debounced.length >= 2,
  });
  // Load cellar for duplicate detection (only when adding, not editing)
  const cellarQ = useQuery({
    queryKey: ['cellar.bottles', { status: 'cellared', limit: 100 }],
    queryFn: () => Bottles.list({ status: 'cellared', limit: 100 }),
    enabled: open && !isEdit,
    staleTime: 30_000,
  });

  const doCreate = (payload: BottleCreate) => create.mutate(payload);

  const create = useMutation({
    mutationFn: (payload: BottleCreate) => Bottles.create(payload),
    onSuccess: (b) => {
      push({ title: 'Lagt i kjelleren', description: b.name, tone: 'success' });
      void qc.invalidateQueries({ queryKey: ['cellar.bottles'] });
      void qc.invalidateQueries({ queryKey: ['cellar.stats'] });
      void qc.invalidateQueries({ queryKey: ['locations.tree'] });
      void qc.invalidateQueries({ queryKey: ['recommendations'] });
      onAdded?.(b.id);
      setDraft({});
      setSearch('');
      onClose();
    },
    onError: (err: Error) => {
      // BOTTLE_LIMIT_REACHED (free plan, 50 bottles) gets the inline
      // UpgradeInlineNotice below the submit button instead of a raw toast.
      if (isEntitlementError(err)) return;
      push({ title: t('common.error'), description: err.message, tone: 'danger' });
    },
  });

  const update = useMutation({
    mutationFn: (payload: BottleCreate) =>
      Bottles.update(existing!.id, payload, existing!.etag),
    onSuccess: (b) => {
      push({ title: 'Oppdatert', description: b.name, tone: 'success' });
      void qc.invalidateQueries({ queryKey: ['cellar.bottles'] });
      void qc.invalidateQueries({ queryKey: ['cellar.stats'] });
      void qc.invalidateQueries({ queryKey: ['bottle', b.id] });
      void qc.invalidateQueries({ queryKey: ['locations.tree'] });
      onUpdated?.(b.id);
      setDraft({});
      onClose();
    },
    onError: (err: Error) => push({ title: t('common.error'), description: err.message, tone: 'danger' }),
  });

  const pickCatalog = (productId: string, name: string, vintage: string | undefined, country: string | undefined, region: string | undefined, imageUrl: string | undefined) => {
    setDraft((d) => ({
      ...d,
      productId,
      catalogId: productId,
      name,
      vintage,
      country,
      region,
      photoUrl: imageUrl,
    }));
  };

  /** Called when user clicks "Legg i kjelleren". Runs duplicate check first. */
  const handleSubmit = () => {
    if (isEdit) {
      update.mutate(draft);
      return;
    }
    const cellar = cellarQ.data?.bottles ?? [];
    const dupe = findDuplicate(draft, cellar);
    if (dupe) {
      setDuplicateMatch({ existing: dupe, payload: draft });
      return;
    }
    doCreate(draft);
  };

  const submitLabel = isEdit ? 'Lagre endringer' : 'Legg i kjelleren';
  const submitting = isEdit ? update.isPending : create.isPending;
  const canSubmit = Boolean(draft.name && (draft.name?.length ?? 0) > 0);

  return (
    <>
      <Drawer
        open={open}
        onClose={onClose}
        title={isEdit ? 'Rediger flaske' : 'Legg til flaske'}
        width="480px"
        footer={
          <div className="flex items-center justify-between gap-2">
            <Button variant="ghost" onClick={onClose}>{t('common.cancel')}</Button>
            <Button
              variant="primary"
              iconLeft={isEdit ? 'check' : 'add-bottle'}
              disabled={!canSubmit}
              loading={submitting}
              onClick={handleSubmit}
              className="flex-1 sm:flex-none"
            >
              {submitLabel}
            </Button>
          </div>
        }
      >
        <div className="grid gap-5">
          {isEntitlementError(create.error) && <UpgradeInlineNotice error={create.error} />}
          {/* Catalog search */}
          <section className="grid gap-3">
            <h3 className="text-xs font-semibold uppercase tracking-widest text-mist">Søk i katalogen</h3>
            <Input
              iconLeft="search"
              placeholder="Domaine, område eller produktnavn..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {catalogQ.isLoading && (
              <div className="grid gap-2">
                <Skeleton height={48} /><Skeleton height={48} />
              </div>
            )}
            {catalogQ.data && (
              <div className="grid gap-1.5 max-h-72 overflow-y-auto pr-1">
                {catalogQ.data.products.map((p) => {
                  const name = p.basic.productLongName ?? p.basic.productShortName ?? '—';
                  const country = (p.origins as { country?: string } | undefined)?.country;
                  const region = (p.origins as { region?: string } | undefined)?.region;
                  const price = (p.prices as { salesPrice?: number } | undefined)?.salesPrice;
                  const isPicked = draft.productId === p.basic.productId;
                  return (
                    <button
                      key={p.basic.productId}
                      type="button"
                      onClick={() => pickCatalog(p.basic.productId, name, p.basic.vintage, country, region, p.imageUrl)}
                      className={`ui-card flex items-center gap-3 p-2 text-left transition-colors ${isPicked ? 'border-candle bg-candle/5' : 'hover:border-line-soft'}`}
                    >
                      <div className="relative grid h-12 w-9 place-items-center overflow-hidden rounded bg-ink-soft">
                        <BottleImage src={p.imageUrl} iconSize={14} />
                      </div>
                      <div className="grid flex-1">
                        <span className="font-serif text-sm leading-tight text-parchment line-clamp-1">{name}</span>
                        <span className="text-[10px] uppercase tracking-widest text-mist">
                          {p.basic.vintage ?? '—'} · {region ?? country ?? '—'}
                        </span>
                      </div>
                      {price && <span className="ui-badge ui-badge--gold">{formatPriceNok(price)}</span>}
                      {isPicked && <Icon name="check" size={14} className="text-candle" />}
                    </button>
                  );
                })}
              </div>
            )}
          </section>

          <div className="ui-divider" />

          {/* Manual fields */}
          <section className="grid gap-3">
            <h3 className="text-xs font-semibold uppercase tracking-widest text-mist">Detaljer</h3>
            <Input
              label="Navn"
              placeholder="Domaine Tawse Chassagne-Montrachet"
              value={draft.name ?? ''}
              onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
            />
            <div className="grid gap-3 sm:grid-cols-2">
              <Input label="Produsent" value={draft.producer ?? ''} onChange={(e) => setDraft((d) => ({ ...d, producer: e.target.value }))} />
              <Input label="Årgang" value={draft.vintage ?? ''} onChange={(e) => setDraft((d) => ({ ...d, vintage: e.target.value }))} />
              <Input label="Land" value={draft.country ?? ''} onChange={(e) => setDraft((d) => ({ ...d, country: e.target.value }))} />
              <Input label="Område" value={draft.region ?? ''} onChange={(e) => setDraft((d) => ({ ...d, region: e.target.value }))} />
              <Input
                label="Antall"
                type="number"
                min={1}
                value={draft.qty ?? 1}
                onChange={(e) => setDraft((d) => ({ ...d, qty: Number(e.target.value) }))}
              />
              <Input label="Strekkode (EAN)" value={draft.barcode ?? ''} onChange={(e) => setDraft((d) => ({ ...d, barcode: e.target.value }))} />
            </div>
            <DrinkWindowRange
              from={draft.drinkFromYear}
              to={draft.drinkToYear}
              onChange={(from, to) =>
                setDraft((d) => ({ ...d, drinkFromYear: from, drinkToYear: to }))
              }
            />
            <Input
              label="Druer (komma-separert)"
              placeholder="Pinot Noir, Chardonnay"
              value={(draft.grapeVarieties ?? []).join(', ')}
              onChange={(e) => setDraft((d) => ({
                ...d,
                grapeVarieties: e.target.value.split(',').map((s) => s.trim()).filter(Boolean),
              }))}
            />
            <Textarea
              label="Notater"
              rows={3}
              value={draft.notes ?? ''}
              onChange={(e) => setDraft((d) => ({ ...d, notes: e.target.value }))}
            />
          </section>

          <div className="ui-divider" />

          {/* Storage */}
          <section className="grid gap-3">
            <h3 className="text-xs font-semibold uppercase tracking-widest text-mist">Plassering</h3>
            <LocationPicker
              value={draft.locationId}
              onChange={(locationId) => setDraft((d) => ({ ...d, locationId }))}
            />
          </section>

        </div>
      </Drawer>

      {/* Duplicate confirmation — rendered outside the drawer so z-index stacks on top */}
      <DuplicateBottleModal
        match={duplicateMatch}
        onAddAnother={(payload) => {
          setDuplicateMatch(null);
          doCreate(payload);
        }}
        onAddWithVintage={(payload, vintage) => {
          setDuplicateMatch(null);
          doCreate({ ...payload, vintage: vintage || payload.vintage });
        }}
        onSkip={() => setDuplicateMatch(null)}
      />
    </>
  );
}
